// Upload surface. In v0 this is a pure mock: accept a File, pretend to POST
// it, return a fake extraction after a short delay. Flip USE_REAL_SERVER at
// build time (or via ?real=1 for quick testing) once the server/capture
// endpoint exists.
//
// Mock return shape matches what the real server will return:
//   { recognized: true, extraction: { vendor, model, hardwareRev?, serial?,
//     macAddress?, wifiSSID?, confidence }, photoId }
// or
//   { recognized: false, reason: "couldn't read the label", photoId }
//
// Either way the UI gets a structured response it can act on without
// having to decide what "ok" means by itself.

const MOCK_LATENCY_MS = 1500;
const MOCK_FAIL_RATE = 0.2; // 1 in 5 mock uploads simulate a bad photo

const MOCK_RECOGNIZED = {
  modem: {
    vendor: 'AT&T',
    model: 'BGW320-500',
    hardwareRev: '500',
    wifiSSID: 'ATTxxxRKfF',
    confidence: 0.91,
  },
  gateway: {
    vendor: 'Tenda',
    model: 'AC6',
    hardwareRev: 'V5',
    macAddress: '04:95:E6:46:D9:00',
    serial: 'E7275024526000969',
    confidence: 0.88,
  },
  ap: {
    vendor: 'Ubiquiti',
    model: 'U6-Lite',
    hardwareRev: '1.0',
    macAddress: 'F4:92:BF:00:00:00',
    confidence: 0.84,
  },
};

const useReal = () => {
  if (import.meta.env.VITE_USE_REAL_SERVER === 'true') return true;
  if (typeof window !== 'undefined') {
    const p = new URLSearchParams(window.location.search);
    if (p.get('real') === '1') return true;
  }
  return false;
};

// Deterministic "fail next upload" toggle for UI testing:
//   localStorage.fieldTripForceFail = '1'  -> next upload fails
//   localStorage.fieldTripForceFail = 'off' -> disable the override
function shouldMockFail() {
  if (typeof window !== 'undefined' && window.localStorage) {
    const v = window.localStorage.getItem('fieldTripForceFail');
    if (v === '1') return true;
    if (v === 'off') return false;
  }
  return Math.random() < MOCK_FAIL_RATE;
}

export async function uploadPhoto({ sessionToken, slot, file, room }) {
  if (useReal()) {
    return uploadReal({ sessionToken, slot, file, room });
  }
  return uploadMock({ slot, file, room });
}

async function uploadMock({ slot, file, room }) {
  if (file && file.size > 0) {
    await file.slice(0, 16).arrayBuffer(); // touch the File API
  }
  await new Promise((resolve) => setTimeout(resolve, MOCK_LATENCY_MS));

  const photoId = `mock-${slot}-${Date.now()}`;

  if (shouldMockFail()) {
    return {
      recognized: false,
      reason: 'label-unreadable',
      photoId,
      slot,
      room: room ?? null,
      mock: true,
    };
  }

  const extraction = MOCK_RECOGNIZED[slot] ?? {
    vendor: 'Unknown',
    model: 'Unknown',
    confidence: 0.4,
  };
  return {
    recognized: true,
    extraction: { ...extraction, slot },
    photoId,
    slot,
    room: room ?? null,
    mock: true,
  };
}

async function uploadReal({ sessionToken, slot, file, room }) {
  if (!sessionToken) {
    throw new Error('missing sessionToken');
  }
  const form = new FormData();
  form.append('slot', slot);
  if (room) form.append('room', room);
  form.append('file', file, file.name || `${slot}.jpg`);
  const res = await fetch(`/api/session/${encodeURIComponent(sessionToken)}/photo`, {
    method: 'POST',
    body: form,
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => '');
    throw new Error(`upload failed: ${res.status} ${msg}`);
  }
  return res.json();
}
